import React, { useState, useRef, useCallback } from 'react';
import { Mic, Square, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MicrophoneButton - Reusable voice recording component
 * 
 * @param {function} onTranscription - Callback with transcribed text
 * @param {function} onPronunciationScore - Callback with pronunciation score (optional)
 * @param {string} expectedText - Text to compare pronunciation against (optional)
 * @param {string} size - Button size: 'sm', 'default', 'lg'
 * @param {string} className - Additional CSS classes
 * @param {boolean} showLabel - Show "Tap to speak" label
 */
export const MicrophoneButton = ({ 
  onTranscription, 
  onPronunciationScore,
  expectedText,
  size = 'default',
  className = '',
  showLabel = false,
  disabled = false,
  maxDuration = 15000
}) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      mediaRecorder.onstop = async () => {
        setIsProcessing(true);
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        
        // Stop all tracks first
        if (streamRef.current) {
          streamRef.current.getTracks().forEach(track => track.stop());
        }

        // Skip if recording too short
        if (audioBlob.size < 1000) {
          toast.warning('Recording too short. Please try again.');
          setIsProcessing(false);
          return;
        }

        try {
          const formData = new FormData();
          formData.append('file', audioBlob, 'recording.webm');
          formData.append('language', 'de');

          // Transcribe the audio
          const transcribeResponse = await axios.post(`${API}/speech/transcribe`, formData);

          const transcribedText = transcribeResponse.data.text || '';
          
          if (onTranscription) {
            onTranscription(transcribedText);
          }

          if (!transcribedText) {
            toast.warning('Could not understand speech. Please try again.');
            setIsProcessing(false);
            return;
          }

          // If expectedText is provided, get pronunciation score
          if (expectedText && onPronunciationScore) {
            try {
              const scoreResponse = await axios.post(`${API}/pronunciation/score`, {
                expected_text: expectedText,
                transcribed_text: transcribedText
              });
              
              onPronunciationScore(scoreResponse.data);
              
              const score = scoreResponse.data.overall_score;
              if (score >= 80) {
                toast.success(`Excellent! ${score}%`);
              } else if (score >= 60) {
                toast.info(`Good try! ${score}%`);
              } else {
                toast.warning(`Keep practicing! ${score}%`);
              }
            } catch (scoreError) {
              // error handled silently
            }
          } else {
            toast.success('Voice recorded!');
          }

        } catch (error) {
          // error handled silently
          const detail = error.response?.data?.detail || '';
          if (error.response?.status === 401) {
            toast.error('Session expired. Please log in again.');
          } else if (detail.includes('budget') || detail.includes('credit')) {
            toast.error('Voice service temporarily unavailable.');
          } else {
            toast.error('Failed to process voice. Please try again.');
          }
        } finally {
          setIsProcessing(false);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
      
      // Auto-stop after maxDuration
      setTimeout(() => {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
          stopRecording();
        }
      }, maxDuration);

    } catch (error) {
      // error handled silently
      toast.error('Could not access microphone. Please allow microphone access.');
    }
  }, [onTranscription, onPronunciationScore, expectedText]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  const handleClick = (e) => {
    e.stopPropagation();
    if (isRecording) {
      stopRecording();
    } else {
      startRecording();
    }
  };

  const sizeClasses = {
    sm: 'h-8 w-8 p-0',
    default: 'h-10 w-10 p-0',
    lg: 'h-14 w-14 p-0'
  };

  const iconSizes = {
    sm: 'h-4 w-4',
    default: 'h-5 w-5',
    lg: 'h-6 w-6'
  };

  return (
    <div className={`inline-flex flex-col items-center gap-1 ${className}`}>
      <Button
        onClick={handleClick}
        disabled={disabled || isProcessing}
        variant={isRecording ? 'destructive' : 'outline'}
        className={`rounded-full ${sizeClasses[size]} ${
          isRecording ? 'animate-pulse bg-red-500 hover:bg-red-600' : 'hover:bg-primary/10'
        }`}
        data-testid="microphone-btn"
      >
        {isProcessing ? (
          <Loader2 className={`${iconSizes[size]} animate-spin`} />
        ) : isRecording ? (
          <Square className={iconSizes[size]} />
        ) : (
          <Mic className={iconSizes[size]} />
        )}
      </Button>
      {showLabel && (
        <span className="text-xs text-muted-foreground">
          {isRecording ? 'Recording...' : isProcessing ? 'Processing...' : 'Tap to speak'}
        </span>
      )}
    </div>
  );
};

export default MicrophoneButton;
